import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const TESTIMONIALS = [
  {
    name: 'Marcus & Lindsey H.',
    neighborhood: 'Harbour Club, Noblesville',
    text: 'We both work at Riverview Health. HR gave us group life insurance but it\'s nowhere near enough to cover our Morse Reservoir home. Benjamin fixed our gap in one call for $41/month. Should have done this years ago.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Craig P.',
    neighborhood: 'Prairie Lakes, Noblesville',
    text: 'I\'ve worked at ADESA for 12 years. Thought my company plan was enough until Benjamin showed me I\'d leave my family $750,000 short. Got my own policy, portable, $32/month. Done deal.',
    stars: 5,
    product: 'Term Life Insurance',
  },
  {
    name: 'Sarah & Tom W.',
    neighborhood: 'Hazel Dell, Noblesville',
    text: 'We just bought a house on the Hazel Dell corridor and needed mortgage protection ASAP. Benjamin had us covered in 48 hours with zero hard sell. Professional, fast, and honest.',
    stars: 5,
    product: 'Mortgage Protection',
  },
  {
    name: 'Denise M.',
    neighborhood: 'Stony Creek, Noblesville',
    text: 'I teach at Hamilton Heights. School coverage disappears if I ever leave. Benjamin helped me get my own portable policy for $22/month. Now I own it forever no matter where I work.',
    stars: 5,
    product: 'Term Life Insurance',
  },
];

const FAQS = [
  {
    q: 'How much does life insurance cost for a Noblesville family?',
    a: 'Most healthy Noblesville adults ages 30–45 pay $25–$50/month for $500,000 in 20-year term coverage. A 35-year-old in good health often pays around $30/month. Because Noblesville families tend to carry larger mortgages near Morse Reservoir and Hazel Dell, we usually recommend $750K–$1.5M in coverage — but a family dinner still costs more per month.',
  },
  {
    q: 'I work at Riverview Health, Hamilton County, or ADESA. Isn\'t my group life insurance enough?',
    a: 'Almost never. Group coverage at most Noblesville employers covers 1–2x your salary — often capped at $50,000–$100,000. Your Noblesville mortgage alone is probably $325,000+. Worse, group coverage disappears the day you leave your job. A private policy is yours forever, no matter where you work.',
  },
  {
    q: 'How fast can I get coverage in Noblesville?',
    a: 'Many carriers offer same-day or next-day approval for healthy applicants. No-exam policies can be active in 24–48 hours. Traditional policies take 2–4 weeks. We\'ll match you to the fastest option based on your situation.',
  },
  {
    q: 'Do I need to drive anywhere, or can we do this by phone or Zoom?',
    a: 'Completely remote — phone, Zoom, or Google Meet. Most Noblesville clients finish everything in a 20-minute lunch break or after the kids are in bed. No office visit needed, ever.',
  },
  {
    q: 'I just bought a home near Morse Reservoir. What coverage do I need?',
    a: 'At minimum, enough to pay off your mortgage if you pass away. On top of that, income replacement for your spouse — roughly 10x your annual salary. For a Noblesville family with a $375,000 home and $90,000 income, we typically recommend $750K–$1M in coverage. The monthly cost usually surprises people — in a good way.',
  },
  {
    q: 'What carriers do you work with for Noblesville residents?',
    a: 'We\'re 100% independent and work with 20+ A-rated carriers including Mutual of Omaha, Protective Life, North American Company, Transamerica, Foresters Financial, Americo, and more. We shop your profile across all of them and bring you the best rate — not the best commission for us.',
  },
];

const NEIGHBORHOODS = [
  'Morse Reservoir', 'Hazel Dell Corridor', 'Prairie Lakes', 'Harbour Club', 'Pebble Brook',
  'White River Area', 'Stony Creek', 'Harrison Pond', 'Deer Path', 'Morse Pointe',
  'Beechwood', 'Sagamore Pkwy Area', '146th St Corridor', 'Noblesville Downtown', 'River Road Area',
];

const SERVICES = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Term Life Insurance',
    desc: 'Maximum coverage for the lowest price. Most Noblesville families ages 25–50 choose a 20 or 30-year term to cover their home and income.',
    cta: 'See Term Life Rates',
    link: '/term-life',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Mortgage Protection',
    desc: 'Noblesville homes average $325,000+. This policy pays your mortgage completely if you pass away — so your family never loses the house.',
    cta: 'Protect My Home',
    link: '/mortgage-protection',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Whole Life Insurance',
    desc: 'Builds real cash value you can use while you\'re alive. Popular with Hamilton County professionals who\'ve maxed their 401(k).',
    cta: 'Explore Whole Life',
    link: '/whole-life',
  },
  {
    icon: 'ri-shield-user-fill',
    title: 'No Medical Exam',
    desc: 'Get approved in 48 hours — zero needles, zero doctor visits. Perfect for busy Noblesville parents and Riverview Health workers.',
    cta: 'Fast Approval',
    link: '/no-medical-exam',
  },
];

export default function NoblesvilleLifeInsurancePage() {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', zip: '', insuranceType: '', bestTime: '' });
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Life Insurance Noblesville IN | Compare 20+ Carriers — Hoosier Life Insurance';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta('description', 'Noblesville, IN life insurance — independent agent compares 20+ carriers to find your lowest rate. Most Noblesville families save money by comparing. Free, 15-minute call. (317) 648-2983.');
    setMeta('keywords', 'life insurance Noblesville Indiana, Noblesville IN life insurance agent, term life insurance Noblesville, mortgage protection Noblesville, Riverview Health Noblesville life insurance, Hamilton County life insurance');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Noblesville, Indiana');
    setMeta('geo.position', '40.0456;-86.0086');
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://hoosierlifeinsurance.com/noblesville-life-insurance';

    // FAQPage + Speakable schema for AI/voice search pricing queries
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-noblesville#faq',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much is life insurance for a 35-year-old in Noblesville, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A healthy 35-year-old in Noblesville, Indiana pays approximately $28–$32 per month for $500,000 in 20-year term life coverage. Males typically pay around $32/month and females around $27/month with preferred health ratings. An independent agent comparing 20+ carriers will find your lowest available rate at no cost to you.',
          },
        },
        {
          '@type': 'Question',
          name: 'What does a $1 million life insurance policy cost in Noblesville, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A $1,000,000 20-year term life insurance policy for a healthy 35-year-old in Noblesville, Indiana costs approximately $45–$55 per month. For a 40-year-old, expect $65–$80/month. Noblesville families with homes near Morse Reservoir averaging $325,000+ often choose $750K–$1.5M in coverage to protect both the mortgage and the family\'s income.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much life insurance do Riverview Health or Hamilton County employees need?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Noblesville employer group plans from Riverview Health, Hamilton County Government, or ADESA typically cover only 1–2x your salary — often $50,000–$100,000. Most Noblesville families need $600,000–$1,500,000 to cover their mortgage, replace income, and fund their children\'s education. An independent policy is portable and fills this gap for $30–$55/month.',
          },
        },
        {
          '@type': 'Question',
          name: 'How quickly can I get life insurance in Noblesville, Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Many carriers offer same-day or next-day approval for healthy Noblesville applicants. No-medical-exam policies can be active within 24–48 hours. Traditional fully underwritten policies take 2–4 weeks. Most Noblesville families complete the entire process in a single 15–20 minute phone or Zoom call — no office visit needed.',
          },
        },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://hoosierlifeinsurance.com/life-insurance-noblesville#speakable',
      speakable: {
        '@type': 'SpeakableSpecification',
        xpath: ['/html/body//*[@id="noblesville-price-summary"]'],
      },
      url: 'https://hoosierlifeinsurance.com/life-insurance-noblesville',
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': 'https://hoosierlifeinsurance.com/noblesville-life-insurance#localbusiness',
      name: 'Hoosier Life Insurance — Noblesville Indiana',
      url: 'https://hoosierlifeinsurance.com/noblesville-life-insurance',
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Noblesville',
        addressRegion: 'IN',
        postalCode: '46060',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.0456,
        longitude: -86.0086,
      },
      areaServed: {
        '@type': 'City',
        name: 'Noblesville',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving Noblesville, Indiana. Riverview Health, Hamilton County Government, ADESA employees, and all Hamilton County families.',
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '127', bestRating: '5', worstRating: '1' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://hoosierlifeinsurance.com/noblesville-life-insurance#breadcrumb',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hoosierlifeinsurance.com' },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: 'https://hoosierlifeinsurance.com/service-areas' },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Noblesville Indiana', item: 'https://hoosierlifeinsurance.com/noblesville-life-insurance' },
      ],
    };

    const schemasToInject = [faqSchema, speakableSchema, localBusinessSchema, breadcrumbSchema];
    schemasToInject.forEach((schema, idx) => {
      const id = `schema-noblesville-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => {
      schemasToInject.forEach((_, idx) => {
        document.getElementById(`schema-noblesville-${idx}`)?.remove();
      });
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append('sms_consent', smsConsent ? 'Yes' : 'No');
      body.append('source_page', 'Noblesville Life Insurance Landing Page');
      const res = await fetch('https://readdy.ai/api/form/d74iorucsbv12mkud5e0', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please call us at (317) 648-2983.');
      }
    } catch {
      setError('Something went wrong. Please call us at (317) 648-2983.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FB]">
      <title>Life Insurance Noblesville IN | Compare 20+ Carriers — Hoosier Life Insurance</title>
      <meta name="description" content="Noblesville life insurance from an independent agent — Riverview Health employees and Hamilton County families compare 20+ carriers to find the lowest rate. Free 15-min call. (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/noblesville-life-insurance" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance Noblesville IN | Compare 20+ Carriers — Hoosier Life Insurance" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/noblesville-life-insurance" />
      <meta name="geo.placename" content="Noblesville, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="40.0456;-86.0086" />
      <Header />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[140px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#D97642]/8 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-fill text-[#F0A070] text-[13px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest">Noblesville, Indiana — Hamilton County</span>
              </div>

              <h1 className="text-[36px] sm:text-[46px] md:text-[52px] font-bold text-white leading-[1.1] mb-4">
                Noblesville Families<br />
                <span className="text-[#D97642]">Deserve Better</span><br />
                Than One-Size Insurance.
              </h1>

              <p className="text-[17px] text-white/80 leading-[1.75] mb-3 max-w-[520px]">
                Most Noblesville families overpay because they never compare. We compare 20+ carriers at once — free, no pressure, in 15 minutes.
              </p>
              <p className="text-[15px] text-white/60 leading-[1.7] mb-8 max-w-[480px]">
                I&apos;m Benjamin Wright — an independent agent in Central Indiana. I don&apos;t work for any carrier. I work for you and your family.
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-building-2-line', text: '20+ Carriers Compared' },
                  { icon: 'ri-time-line', text: '15-Minute Free Call' },
                  { icon: 'ri-lock-line', text: 'No Pressure, Ever' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#D97642] text-[14px]`}></i></div>
                    <span className="text-white/90 text-[13px] font-medium">{b.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#noblesville-quote-form"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line text-[18px]"></i>
                  See My Noblesville Rate
                </a>
                <a
                  href="tel:+13176482983"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-fill text-[18px]"></i>
                  (317) 648-2983
                </a>
              </div>
            </div>

            {/* Right — stats */}
            <div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { value: '#2', label: 'Fastest Growing City in Indiana', sub: 'Hamilton County boom continues' },
                  { value: '$88K+', label: 'Median Household Income', sub: 'Noblesville families thrive' },
                  { value: '$325K+', label: 'Avg Noblesville Home Value', sub: 'Your biggest asset needs protection' },
                  { value: '20+', label: 'Carriers We Compare', sub: 'You always get the best rate' },
                ].map(s => (
                  <div key={s.label} className="bg-white/10 border border-white/20 rounded-xl p-4">
                    <div className="text-[26px] font-black text-[#D97642] leading-none mb-1">{s.value}</div>
                    <div className="text-[13px] font-semibold text-white leading-tight">{s.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
              <img
                src="https://public.readdy.ai/ai/img_res/a916aa2050d1a97b9f410d322841311b.jpg"
                alt="Noblesville Indiana Family Life Insurance Morse Reservoir"
                className="w-full h-[220px] object-cover object-top rounded-2xl"
                loading="eager"
                decoding="async"
                width={600}
                height={380}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Proof Bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-fill text-[#F59E0B] text-[18px]"></i>
              ))}
            </div>
            <span className="text-[15px] font-bold text-[#1A3A52]">4.9 / 5</span>
            <span className="text-[13px] text-[#718096]">from 127 Indiana families</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">500+</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Families Protected</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">$0</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Cost to You</div>
            </div>
            <div className="w-px h-10 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-[22px] font-black text-[#D97642]">15 min</div>
              <div className="text-[11px] text-[#718096] uppercase tracking-wide">Average Call Time</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[13px] text-[#4A5568]">
            <i className="ri-shield-check-fill text-[#D97642] text-[16px]"></i>
            <span>Indiana Licensed · <strong>Not a Call Center</strong></span>
          </div>
        </div>
      </div>

      {/* ── Employer Section (The Big Local Hook) ── */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 mb-4">
                <i className="ri-error-warning-line text-red-500 text-[14px]"></i>
                <span className="text-red-600 text-[12px] font-semibold uppercase tracking-wide">What Noblesville Employers Don&apos;t Tell You</span>
              </div>
              <h2 className="text-[32px] md:text-[38px] font-bold text-[#1A3A52] leading-[1.2] mb-5">
                Your Riverview Health or Hamilton County insurance won&apos;t follow you home.
              </h2>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-4">
                Noblesville is one of Indiana&apos;s biggest job hubs. Thousands of people work at Riverview Health, Hamilton County government, ADESA, Kohl&apos;s distribution, and Indiana Premium Outlets. Most have group life insurance. Almost none have enough.
              </p>
              <p className="text-[16px] text-[#4A5568] leading-[1.8] mb-6">
                Group plans typically cover 1–2x your salary. Your Noblesville mortgage is probably $300,000+. Your family needs 10x your income to actually be okay — and group insurance disappears the moment you change employers.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  'Riverview Health group plan covers 1–2x salary (usually not enough)',
                  'Hamilton County employee coverage ends when you leave government service',
                  'ADESA, Kohl\'s, Outlet stores — all the same story',
                  'Rates go up every year you wait to get your own policy',
                  'A health change tomorrow could disqualify you from future coverage',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-red-100 rounded-full mt-0.5 flex-shrink-0">
                      <i className="ri-close-line text-red-500 text-[12px]"></i>
                    </div>
                    <span className="text-[14px] text-[#4A5568]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-white">
              <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/30 rounded-full px-3 py-1 mb-5">
                <i className="ri-check-double-line text-[#D97642] text-[14px]"></i>
                <span className="text-[#F0A070] text-[12px] font-semibold uppercase tracking-wide">The Fix — Takes 15 Minutes</span>
              </div>
              <h3 className="text-[26px] font-bold text-white leading-tight mb-4">
                What a private policy does for your Noblesville family:
              </h3>
              {[
                { icon: 'ri-home-heart-line', title: 'Pays off your Noblesville home completely', desc: 'Your family stays on Morse Reservoir or Hazel Dell — no matter what.' },
                { icon: 'ri-team-line', title: 'Replaces your income for 10–20 years', desc: 'Your spouse doesn\'t have to figure out money while grieving.' },
                { icon: 'ri-graduation-cap-line', title: 'Funds your kids\' futures', desc: 'Hamilton Heights, HSE — their path stays on track, even if yours changes.' },
                { icon: 'ri-lock-line', title: 'Follows you from job to job, forever', desc: 'Riverview, county, ADESA — doesn\'t matter. It\'s yours permanently.' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-[18px]`}></i>
                  </div>
                  <div>
                    <div className="text-[14px] font-semibold text-white">{item.title}</div>
                    <div className="text-[13px] text-white/60">{item.desc}</div>
                  </div>
                </div>
              ))}
              <a
                href="#noblesville-quote-form"
                className="block text-center mt-6 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer"
              >
                Get My Noblesville Rate — Free
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Employer Callout Strip ── */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-5">We help employees from Noblesville&apos;s largest employers close the coverage gap</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { icon: 'ri-hospital-line', name: 'Riverview Health' },
              { icon: 'ri-government-line', name: 'Hamilton County Gov\'t' },
              { icon: 'ri-car-line', name: 'ADESA Auto Auctions' },
              { icon: 'ri-store-2-line', name: 'Kohl\'s Distribution' },
              { icon: 'ri-shopping-bag-3-line', name: 'Indiana Premium Outlets' },
              { icon: 'ri-tools-line', name: 'Arconic / Manufacturing' },
              { icon: 'ri-book-open-line', name: 'Hamilton Heights Schools' },
              { icon: 'ri-building-line', name: 'City of Noblesville' },
            ].map(e => (
              <div key={e.name} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-xl text-[13px] font-semibold text-[#1A3A52]">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${e.icon} text-[#D97642] text-[15px]`}></i></div>
                {e.name}
              </div>
            ))}
          </div>
          <p className="text-center text-[13px] text-[#718096] mt-4">
            Work somewhere else? We cover <strong>all</strong> Noblesville and Hamilton County residents. <a href="#noblesville-quote-form" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Get your free rate.</a>
          </p>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="py-16 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Life Insurance for Noblesville Families</h2>
            <p className="text-[16px] text-[#718096] max-w-[600px] mx-auto">
              From Morse Reservoir to Prairie Lakes — we help Noblesville families find the right coverage at the right price.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map(svc => (
              <a key={svc.title} href={svc.link} className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] hover:bg-white transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${svc.icon} text-white text-[22px]`}></i>
                </div>
                <h3 className="text-[16px] font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors">{svc.title}</h3>
                <p className="text-[13px] text-[#4A5568] leading-[1.7] mb-4">{svc.desc}</p>
                <div className="flex items-center gap-1 text-[13px] text-[#D97642] font-semibold">
                  <span>{svc.cta}</span>
                  <i className="ri-arrow-right-line text-[14px]"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Does Life Insurance Cost in Noblesville? (AI/AEO pricing section) ── */}
      <section id="noblesville-price-summary" className="py-14 bg-[#F7F9FB] border-y border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-8">
            <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-2">What Does Life Insurance Cost in Noblesville, Indiana?</h2>
            <p className="text-[15px] text-[#718096] max-w-[620px] mx-auto">
              Rates below are for healthy, non-smoking Noblesville residents. Indiana rates run 8–12% below the national average. We compare 20+ carriers to find your lowest rate.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-5 py-3.5 text-left font-semibold">Age</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Coverage</th>
                  <th className="px-5 py-3.5 text-left font-semibold">Term</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Male/Month</th>
                  <th className="px-5 py-3.5 text-center font-semibold">Female/Month</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { age: '30', coverage: '$500,000', term: '20-year', male: '$28', female: '$23' },
                  { age: '35', coverage: '$500,000', term: '20-year', male: '$32', female: '$27' },
                  { age: '35', coverage: '$1,000,000', term: '20-year', male: '$51', female: '$43' },
                  { age: '40', coverage: '$500,000', term: '20-year', male: '$45', female: '$38' },
                  { age: '40', coverage: '$750,000', term: '20-year', male: '$63', female: '$53' },
                  { age: '45', coverage: '$500,000', term: '20-year', male: '$68', female: '$56' },
                  { age: '50', coverage: '$250,000', term: '20-year', male: '$72', female: '$60' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-5 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.coverage}</td>
                    <td className="px-5 py-3 text-[#4A5568]">{row.term}</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.male}/mo</td>
                    <td className="px-5 py-3 text-center font-bold text-[#D97642]">{row.female}/mo</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[12px] text-[#718096] italic">*Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and individual health profile.</p>
            <a href="#noblesville-quote-form" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[16px]"></i>
              Get My Exact Noblesville Rate
            </a>
          </div>
        </div>
      </section>

      {/* ── Quote Form + Testimonials ── */}
      <section id="noblesville-quote-form" className="py-16 md:py-24 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Testimonials */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="mb-2">
                <h2 className="text-[26px] font-bold text-[#1A3A52] mb-2">What Noblesville Families Are Saying</h2>
                <p className="text-[14px] text-[#718096]">Real people. Real neighborhoods. No scripts.</p>
              </div>
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(s => (
                      <i key={s} className="ri-star-fill text-[#F59E0B] text-[14px]"></i>
                    ))}
                  </div>
                  <p className="text-[14px] text-[#4A5568] leading-[1.7] mb-3 italic">&ldquo;{t.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[13px] font-bold text-[#1A3A52]">{t.name}</div>
                      <div className="text-[12px] text-[#718096]">{t.neighborhood}</div>
                    </div>
                    <span className="text-[11px] bg-[#D97642]/10 text-[#D97642] font-semibold px-2 py-1 rounded-full whitespace-nowrap">{t.product}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 p-8">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 bg-[#D97642]/10 border border-[#D97642]/20 rounded-full px-3 py-1 mb-3">
                    <i className="ri-map-pin-line text-[#D97642] text-[13px]"></i>
                    <span className="text-[#D97642] text-[12px] font-semibold">Serving All Noblesville Neighborhoods</span>
                  </div>
                  <h2 className="text-[24px] font-bold text-[#1A3A52] mb-1">Get Your Free Noblesville Quote</h2>
                  <p className="text-[14px] text-[#718096]">Takes 2 minutes. No obligation. We respond within 24 hours.</p>
                </div>

                {submitted ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                      <i className="ri-check-double-line text-green-600 text-[32px]"></i>
                    </div>
                    <h3 className="text-[22px] font-bold text-[#1A3A52] mb-2">You&apos;re all set!</h3>
                    <p className="text-[15px] text-[#4A5568] mb-6">We&apos;ll review your info and reach out within 24 hours with Noblesville-specific options.</p>
                    <button onClick={() => setSubmitted(false)} className="px-6 py-3 bg-[#D97642] text-white font-semibold rounded-xl hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap text-[14px]">
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form id="noblesville-quote-form-el" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="source_page" value="Noblesville Life Insurance Landing Page" />

                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Full Name <span className="text-[#D97642]">*</span></label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-gray-400 text-[15px]"></i></div>
                        <input type="text" name="name" required placeholder="Jane Smith" value={formData.name} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-phone-line text-gray-400 text-[15px]"></i></div>
                          <input type="tel" name="phone" required placeholder="(317) 000-0000" value={formData.phone} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-mail-line text-gray-400 text-[15px]"></i></div>
                          <input type="email" name="email" required placeholder="jane@example.com" value={formData.email} onChange={handleChange} className="w-full pl-9 pr-3 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Noblesville ZIP Code <span className="text-[#D97642]">*</span></label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-gray-400 text-[15px]"></i></div>
                          <input type="text" name="zip" required placeholder="46060" pattern="[0-9]{5}" maxLength={5} value={formData.zip} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">What Are You Looking For?</label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-shield-line text-gray-400 text-[15px]"></i></div>
                          <select name="insuranceType" value={formData.insuranceType} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                            <option value="">Help me decide</option>
                            <option value="term_life">Term Life Insurance</option>
                            <option value="whole_life">Whole Life Insurance</option>
                            <option value="mortgage_protection">Mortgage Protection</option>
                            <option value="final_expense">Final Expense Insurance</option>
                            <option value="annuity">Annuity / Retirement</option>
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Best Time to Call</label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-gray-400 text-[15px]"></i></div>
                        <select name="bestTime" value={formData.bestTime} onChange={handleChange} className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] transition-colors bg-white appearance-none cursor-pointer">
                          <option value="">Any time works</option>
                          <option value="morning">Morning (8am – 12pm)</option>
                          <option value="afternoon">Afternoon (12pm – 5pm)</option>
                          <option value="evening">Evening (5pm – 9pm)</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none"><i className="ri-arrow-down-s-line text-gray-400 text-[16px]"></i></div>
                      </div>
                    </div>

                    {error && (
                      <p className="text-[13px] text-red-500 flex items-center gap-1.5">
                        <i className="ri-error-warning-line text-[15px]"></i>
                        {error}
                      </p>
                    )}

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="sms_consent" required checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#D97642] flex-shrink-0 cursor-pointer" />
                        <span className="text-[12px] text-gray-600 leading-relaxed">
                          <strong className="text-gray-800">SMS Consent (Required):</strong> I agree to receive text messages from Hoosier Life Insurance about my quote request. Reply STOP anytime.{' '}
                          <a href="/privacy-policy" className="text-[#D97642] underline">Privacy Policy</a>
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting || !smsConsent}
                      className="w-full py-4 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin text-[18px]"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-[16px]"></i> Show Me My Noblesville Rates — Free</>
                      )}
                    </button>

                    <p className="text-[11px] text-[#718096] text-center">No obligation. We never sell your information.</p>
                  </form>
                )}
              </div>

              {/* Call option */}
              <div className="mt-4 flex items-center gap-4 bg-[#1A3A52] rounded-xl p-5">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-full flex-shrink-0">
                  <i className="ri-phone-fill text-white text-[20px]"></i>
                </div>
                <div>
                  <div className="text-[12px] text-white/60 uppercase tracking-wider font-semibold">Prefer to call directly?</div>
                  <a href="tel:+13176482983" className="text-[22px] font-bold text-white hover:text-[#D97642] transition-colors cursor-pointer">(317) 648-2983</a>
                  <div className="text-[12px] text-white/60">Mon–Fri 9AM–6PM · Sat 10AM–2PM · Evenings by appointment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Neighborhoods ── */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-6 text-center">We Serve All Noblesville Neighborhoods</h2>
          <div className="flex flex-wrap gap-2 justify-center">
            {NEIGHBORHOODS.map(n => (
              <div key={n} className="flex items-center gap-1.5 px-3 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] font-medium text-[#1A3A52]">
                <i className="ri-map-pin-line text-[#D97642] text-[14px]"></i>
                {n}
              </div>
            ))}
          </div>
          <p className="text-[14px] text-[#718096] text-center mt-6">
            Don&apos;t see your area? We serve all of Hamilton County. <a href="tel:+13176482983" className="text-[#D97642] font-semibold hover:underline cursor-pointer">Just call us.</a>
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-20 bg-[#F7F9FB]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[32px] md:text-[40px] font-bold text-[#1A3A52] mb-3">Noblesville Life Insurance FAQ</h2>
            <p className="text-[15px] text-[#718096]">The questions Noblesville families ask us most.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className={`rounded-xl overflow-hidden border transition-all ${openFaq === i ? 'border-[#D97642]/50 bg-white' : 'border-gray-200 bg-white hover:border-[#D97642]/30'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer gap-3">
                  <span className="text-[15px] font-semibold text-[#1A3A52] leading-snug">{faq.q}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#D97642] rotate-180' : 'bg-gray-100'}`}>
                    <i className={`ri-arrow-down-s-line text-[18px] ${openFaq === i ? 'text-white' : 'text-gray-500'}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-[14px] text-[#4A5568] leading-[1.8]">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-16 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-[32px] md:text-[40px] font-bold text-white mb-4">
            Noblesville is growing fast. Your protection should keep up.
          </h2>
          <p className="text-[17px] text-white/75 leading-[1.7] mb-8 max-w-[580px] mx-auto">
            15 minutes. Zero pressure. You&apos;ll walk away knowing exactly what your family needs — and exactly what it costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#noblesville-quote-form" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white text-[16px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-[18px]"></i>
              Get My Free Rate Now
            </a>
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 text-white text-[16px] font-semibold rounded-xl hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-check-line text-[18px]"></i>
              Schedule a Call
            </a>
          </div>
          <p className="text-[13px] text-white/40 mt-6">Serving Noblesville families in 46060, 46061, and 46062</p>
        </div>
      </section>

      {/* ── Indiana City Cross-Link Cluster ── */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Hamilton County</p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {[
              { label: 'Life Insurance Fishers', href: '/life-insurance-fishers', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Carmel', href: '/life-insurance-carmel', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Hamilton County', href: '/life-insurance-hamilton-county', icon: 'ri-map-2-line' },
            ].map(link => (
              <a key={link.label} href={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </a>
            ))}
          </div>
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Statewide Indiana</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
            ].map(link => (
              <a key={link.label} href={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </a>
            ))}
          </div>
          {/* Blog articles — topical authority */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-2">Learn More From Our Blog</p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'Mortgage Protection Insurance in Indianapolis', href: '/blog/mortgage-protection-indianapolis' },
                { label: 'Life Insurance for Indiana Families', href: '/blog/life-insurance-for-families-indianapolis' },
                { label: 'How Much Life Insurance Does an Indiana Family Need?', href: '/blog/how-much-life-insurance-indiana' },
                { label: 'Term Life Insurance in Indianapolis & Indiana', href: '/blog/term-life-insurance-indianapolis' },
                { label: 'No Medical Exam Life Insurance in Indiana', href: '/blog/no-medical-exam-life-insurance-indiana' },
              ].map(blog => (
                <a key={blog.href} href={blog.href} className="flex items-center gap-2 px-4 py-2 bg-[#FDF6F0] border border-[#D97642]/20 rounded-lg text-[13px] text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-article-line text-[#D97642] text-[14px]"></i></div>
                  {blog.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
